import { useEffect, useRef, useState } from 'react';

interface Props {
  onGameEnd?: (winner: string | null) => void;
  isHost: boolean;
  sendGameState?: (state: any) => void;
  gameId?: string;
  isOffline?: boolean;
}

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 400;
const PADDLE_HEIGHT = 12;
const PADDLE_WIDTH = 100;
const BALL_RADIUS = 8;
const BALL_SPEED = 5;

export default function CoopPong({ onGameEnd, isHost, sendGameState }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState(0);

  // All mutable game state in refs to avoid stale closures in the game loop
  const myPaddleRef = useRef(CANVAS_WIDTH / 2);
  const opponentPaddleRef = useRef(CANVAS_WIDTH / 2);
  const ballPosRef = useRef({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 });
  const ballVelRef = useRef({ x: BALL_SPEED, y: BALL_SPEED });
  const scoreRef = useRef(0);
  const gameOverRef = useRef(false);

  // Listen for opponent paddle and ball updates from Firebase
  useEffect(() => {
    const handleGameState = (e: CustomEvent) => {
      const state = e.detail;
      if (isHost && state.type === 'PADDLE2_MOVE') {
        opponentPaddleRef.current = state.position;
      } else if (!isHost && state.type === 'PADDLE1_MOVE') {
        opponentPaddleRef.current = state.position;
      }
      // Guest receives authoritative ball position from host
      if (!isHost && state.type === 'BALL_UPDATE') {
        ballPosRef.current = state.position;
        ballVelRef.current = state.velocity;
        scoreRef.current = state.score;
        setScore(state.score);
      }
    };

    window.addEventListener('game_state', handleGameState as EventListener);
    return () => window.removeEventListener('game_state', handleGameState as EventListener);
  }, [isHost]);

  // Mouse control — send paddle position to opponent
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = Math.max(
        PADDLE_WIDTH / 2,
        Math.min(CANVAS_WIDTH - PADDLE_WIDTH / 2, e.clientX - rect.left)
      );
      myPaddleRef.current = mouseX;
      sendGameState?.({
        type: isHost ? 'PADDLE1_MOVE' : 'PADDLE2_MOVE',
        position: mouseX
      });
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    return () => canvas.removeEventListener('mousemove', handleMouseMove);
  }, [isHost, sendGameState]);

  // Game loop — stable, runs once
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const gameLoop = setInterval(() => {
      if (gameOverRef.current) return;

      // Top paddle = host, bottom paddle = guest
      const topPaddle = isHost ? myPaddleRef.current : opponentPaddleRef.current;
      const bottomPaddle = isHost ? opponentPaddleRef.current : myPaddleRef.current;

      // Only host runs authoritative ball physics
      if (isHost) {
        let { x, y } = ballPosRef.current;
        let { x: vx, y: vy } = ballVelRef.current;

        x += vx;
        y += vy;

        // Side walls
        if (x <= BALL_RADIUS) { x = BALL_RADIUS; vx = Math.abs(vx); }
        if (x >= CANVAS_WIDTH - BALL_RADIUS) { x = CANVAS_WIDTH - BALL_RADIUS; vx = -Math.abs(vx); }

        // Top paddle collision
        if (y <= PADDLE_HEIGHT + BALL_RADIUS && Math.abs(x - topPaddle) < PADDLE_WIDTH / 2) {
          y = PADDLE_HEIGHT + BALL_RADIUS;
          vy = Math.abs(vy);
          scoreRef.current += 1;
          setScore(scoreRef.current);
        }

        // Bottom paddle collision
        if (y >= CANVAS_HEIGHT - PADDLE_HEIGHT - BALL_RADIUS && Math.abs(x - bottomPaddle) < PADDLE_WIDTH / 2) {
          y = CANVAS_HEIGHT - PADDLE_HEIGHT - BALL_RADIUS;
          vy = -Math.abs(vy);
          scoreRef.current += 1;
          setScore(scoreRef.current);
        }

        // Ball escaped — game over
        if (y <= 0 || y >= CANVAS_HEIGHT) {
          gameOverRef.current = true;
          onGameEnd?.('Score: ' + scoreRef.current);
          clearInterval(gameLoop);
          return;
        }

        ballPosRef.current = { x, y };
        ballVelRef.current = { x: vx, y: vy };

        // Sync ball state to guest
        sendGameState?.({
          type: 'BALL_UPDATE',
          position: { x, y },
          velocity: { x: vx, y: vy },
          score: scoreRef.current
        });
      }

      // --- Render ---
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Centre line
      ctx.setLineDash([10, 10]);
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, CANVAS_HEIGHT / 2);
      ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT / 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Paddles
      ctx.fillStyle = isHost ? '#ec4899' : '#8b5cf6';
      ctx.beginPath();
      ctx.roundRect(topPaddle - PADDLE_WIDTH / 2, 0, PADDLE_WIDTH, PADDLE_HEIGHT, 4);
      ctx.fill();

      ctx.fillStyle = isHost ? '#8b5cf6' : '#ec4899';
      ctx.beginPath();
      ctx.roundRect(bottomPaddle - PADDLE_WIDTH / 2, CANVAS_HEIGHT - PADDLE_HEIGHT, PADDLE_WIDTH, PADDLE_HEIGHT, 4);
      ctx.fill();

      // Ball
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(ballPosRef.current.x, ballPosRef.current.y, BALL_RADIUS, 0, Math.PI * 2);
      ctx.fill();

      // Score
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.font = 'bold 48px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(String(scoreRef.current), CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 10);

      // Player labels
      ctx.font = '14px sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillText(isHost ? 'You' : 'Opponent', CANVAS_WIDTH / 2, 30);
      ctx.fillText(isHost ? 'Opponent' : 'You', CANVAS_WIDTH / 2, CANVAS_HEIGHT - 20);

    }, 1000 / 60);

    return () => clearInterval(gameLoop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally stable — all state accessed via refs

  return (
    <div className="flex flex-col items-center">
      <div className="mb-4">
        <span className="text-xl font-bold">Score: {score}</span>
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="border border-gray-700 rounded-lg cursor-none"
      />
      <div className="mt-4 text-sm text-gray-300">
        {isHost ? 'You control the top paddle (pink)' : 'You control the bottom paddle (pink)'} — move your mouse
      </div>
    </div>
  );
}
